import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { COLOUR_HEX } from '../../components/ColorSwatchFilter/colorMap';
import css from './ListingPage.module.css';

/**
 * Always-visible "quick facts" bar at the top of a product page. Pulls the
 * five universal Materials globals from publicData + listingFields config
 * and renders each as a label-value row:
 *
 *   In Stock           ✓ Yes
 *   Samples Available  ✓ Yes
 *   Colour             ● Light Blue  ● Sage
 *   Price Tier         Mid-Range
 *   Use                Interior, Exterior
 *
 * Rows where the listing has no value for that field are skipped — a paint
 * listing without an `interior_exterior` tag just doesn't show that row.
 *
 * The component is data-driven from listingFieldConfigs so adding a new
 * universal field to the bar is just an entry in QUICK_FACT_KEYS below.
 */

// Order matters — controls the display order of rows.
// Note: a single `exterior` boolean, labelled "Exterior rated" (the
// interior+exterior pair collapsed 2026-05-28 — interior is the default for
// all materials, so only the exterior exception carries signal).
const QUICK_FACT_KEYS = [
  'in_stock',
  'samples_available',
  'colour_family',
  'price_tier',
  'exterior',
];

// Resolve a listingField's enum-option label from its key. enumOptions on
// the config use { option, label }, so a value array like ['light-blue']
// → ['Light Blue'].
const labelFor = (config, optionKey) =>
  config?.enumOptions?.find(o => `${o.option}` === `${optionKey}`)?.label || optionKey;

const renderValue = (config, value) => {
  if (value == null || value === '') return null;
  const { schemaType, key } = config;

  if (schemaType === 'boolean') {
    return value ? <FormattedMessage id="ListingPage.fixedSpec.yes" defaultMessage="Yes" />
                 : <FormattedMessage id="ListingPage.fixedSpec.no"  defaultMessage="No"  />;
  }
  if (schemaType === 'enum') {
    return labelFor(config, value);
  }
  if (schemaType === 'multi-enum') {
    if (!Array.isArray(value) || value.length === 0) return null;
    // Special-case colour_family: render hex dot + label per selected colour.
    if (key === 'colour_family') {
      return (
        <span className={css.fixedSpecColourValues}>
          {value.map(v => {
            const hex = COLOUR_HEX[v];
            return (
              <span key={v} className={css.fixedSpecColourChip}>
                <span
                  className={css.fixedSpecColourDot}
                  style={hex ? { backgroundColor: hex } : undefined}
                  aria-hidden="true"
                />
                {labelFor(config, v)}
              </span>
            );
          })}
        </span>
      );
    }
    return value.map(v => labelFor(config, v)).join(', ');
  }
  return String(value);
};

const SectionFixedSpecs = props => {
  const { publicData, listingFieldConfigs } = props;
  if (!publicData) return null;

  const rows = QUICK_FACT_KEYS.map(key => {
    const config = listingFieldConfigs?.find(c => c.key === key);
    if (!config) return null;
    const value = publicData[key];
    const rendered = renderValue(config, value);
    if (rendered == null) return null;
    return {
      key,
      label: config.filterConfig?.label || config.showConfig?.label || key,
      value: rendered,
    };
  }).filter(Boolean);

  if (rows.length === 0) return null;

  return (
    <section className={css.fixedSpecsSection} aria-label="Quick facts">
      <ul className={css.fixedSpecsList}>
        {rows.map(row => (
          <li key={row.key} className={css.fixedSpecRow}>
            <span className={css.fixedSpecLabel}>{row.label}</span>
            <span className={css.fixedSpecValue}>{row.value}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default SectionFixedSpecs;
